<template>
  <div class="flow" :style="{ gridArea: flowPosition }">
    <svg class="flow__svg" @click="resetSelection">
      <vertical-lines />
      <svg-definitions />

      <custom-path
        v-for="path in connections"
        :id="path.id"
        :key="path.id"
        :attributes="path.attributes"
        :start-shape-id="path.startShapeId"
        :end-shape-id="path.endShapeId"
      />

      <custom-circle v-for="shapeId in shapes" :id="shapeId" :key="shapeId"></custom-circle>
      <!-- Overlays -->
      <g>
        <line-overlay
          v-for="path in connections"
          :id="path.id"
          :key="path.id"
          :attributes="path.attributes"
          :start-shape-id="path.startShapeId"
          :end-shape-id="path.endShapeId"
        />
      </g>
      <selection />
    </svg>
    <div class="flow__icon" @click="showFlowContextMenu">
      <custom-icon class-name="icon-dots-horizontal" />
    </div>
  </div>
</template>

<script>
import hotkeys from "hotkeys-js";

import CustomCircle from "./CustomCircle.vue";
import CustomPath from "./CustomPath.vue";
import SvgDefinitions from "./SvgDefinitions.vue";
import Selection from "./Selection.vue";
import LineOverlay from "./overlays/LineOverlay.vue";
import VerticalLines from "./VerticalLines.vue";
import { HotkeyScopes, Direction } from "../../../../../utils/constants";
import { RowTypes, ColumnTypes } from "../../../cell/CellConstants";
import { RADIUS, SPACE_BETWEEN_VLINES } from "./ElementUtils";
import { SelectionType } from "./Constants";
import DesignerMixin from "../../../DesignerMixin";
import CustomIcon from "../../../../commons/CustomIcon.vue";

export default {
  components: {
    CustomIcon,
    CustomCircle,
    CustomPath,
    SvgDefinitions,
    Selection,
    LineOverlay,
    VerticalLines,
  },
  mixins: [DesignerMixin],
  props: ["height"],
  computed: {
    shapes() {
      return this.$store.state.customFlow.shapes;
    },
    shapeMap() {
      return this.$store.state.customFlow.shapeMap;
    },
    connections() {
      return Object.values(this.$store.state.customFlow.connections);
    },
    selectionData() {
      return this.$store.state.customFlow.selection;
    },
    flowPosition() {
      const flowIndex = this.columns.findIndex((item) => item.type === ColumnTypes.Flow);
      const firstActivityIndex = this.rows.findIndex((item) => item.type === RowTypes.Activity);
      return `${firstActivityIndex + 2} / ${flowIndex + 2} / -2 / span 1`;
    },
  },
  mounted() {
    hotkeys("cmd+shift+b,ctrl+shift+b", HotkeyScopes.ProcessFlow, (event, handler) => {
      event.preventDefault();
      this.$store.dispatch("customFlow/setShapeType");
    });

    hotkeys("delete", HotkeyScopes.ProcessFlow, (event, handler) => {
      this.$store.dispatch("customFlow/deleteClick");
    });

    // Move circle with ctrl + arrow keys.
    this.moveCircle();
    this.selectCircle();
  },
  beforeUnmount() {
    hotkeys.unbind(
      "delete,cmd+shift+b,ctrl+shift+b,ctrl+right,cmd+right, ctrl+left, cmd+left,tab,shift+tab",
      HotkeyScopes.ProcessFlow
    );
  },
  methods: {
    showFlowContextMenu(e) {
      this.$store.dispatch("customFlow/setFlowContextMenu", {
        x: e.x + 15,
        y: e.y,
      });
    },
    resetSelection(e) {
      if (e.target.tagName === "circle" || e.target.tagName === "line") {
        return;
      }
      this.$store.dispatch("customFlow/resetSelection");
      this.$store.dispatch("customFlow/resetArrowData");
      this.$store.dispatch("setScope", HotkeyScopes.ProcessFlow); //set scope to be able to move the shape with keyboard.
    },
    setSelection(shape, cx = shape.attributes.cx, cy = shape.attributes.cy) {
      const minPoint = [cx - RADIUS, cy - RADIUS];
      const maxPoint = [cx + RADIUS, cy + RADIUS];

      const data = {
        minPoint,
        maxPoint,
        id: shape.id,
        type: SelectionType.Circle,
      };
      this.$store.dispatch("customFlow/setSelection", data);
    },
    moveTo(direction) {
      const selectedShape = this.$store.state.customFlow.shapeMap[this.selectionData.id];
      let cx = selectedShape.attributes.cx;
      let cy = selectedShape.attributes.cy;
      if (direction === Direction.Right) cx += SPACE_BETWEEN_VLINES;
      if (direction === Direction.Left) cx -= SPACE_BETWEEN_VLINES;

      this.setSelection(selectedShape, cx, cy);
      this.$store.dispatch("customFlow/moveShape", { shapeId: this.selectionData.id, cx });
    },
    moveCircle() {
      hotkeys("ctrl+right, cmd+ right, ctrl+left,cmd+left", HotkeyScopes.ProcessFlow, (event, handler) => {
        event.preventDefault();
        if (!this.selectionData.id) return;

        switch (handler.key) {
          case "ctrl+right":
          case "cmd+right":
            this.moveTo(Direction.Right);
            break;
          case "ctrl+left":
          case "cmd+left":
            this.moveTo(Direction.Left);
            break;
        }
      });
    },
    selectCircle() {
      hotkeys("tab, shift+tab", HotkeyScopes.ProcessFlow, (event, handler) => {
        event.preventDefault();
        const currentShapeIndex = this.shapes.findIndex((shapeId) => shapeId === this.selectionData.id);
        const nextShapeIndex =
          (currentShapeIndex + (handler.key === "tab" ? 1 : this.shapes.length - 1)) % this.shapes.length;
        const nextShapeId = this.shapes[nextShapeIndex];
        this.setSelection(this.shapeMap[nextShapeId]);
      });
    },
  },
};
</script>

<style lang="less" scoped>
.flow {
  position: relative;
  z-index: 5;
  cursor: default;
  &__svg {
    width: 100%;
    height: 100%;
  }
  &__icon {
    position: absolute;
    top: -32px;
    right: 5px;
    cursor: pointer;
  }
}
</style>
